// updated version 1.0.1

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirmMode } from '../contexts/FirmModeContext';
import FirmCaseContextBar from '../components/FirmCaseContextBar';
import { useIsMobile } from '../hooks/useIsMobile';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { buildFirmCaseContextPayload } from '../utils/firmCaseContextPayload';
import './UnifiedPage.css';
import { formatMessageText } from '../utils/textUtils';
import { trackInteraction } from '../utils/sessionUtils';
import { Copy, ThumbsUp, ThumbsDown, Share } from 'lucide-react';
import UpgradeModal from '../components/UpgradeModal';
import InlinePaywallPreviewCard from '../components/InlinePaywallPreviewCard';
import { buildPaywallPreviewParts } from '../utils/paywallPreview';
import {
  extractAssistantReplyText,
  extractWebhookActions,
  extractWebhookBilling,
} from '../utils/webhookResponseParse';

const LegalDiscussionPage = () => {
  const { t, isRTL } = useLanguage();
  const { user, isLoading: authLoading } = useAuth();
  const { isFirmMode, activeFirmCase } = useFirmMode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoSendDoneRef = useRef(false);

  // Sidebar
  const sidebarRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isMobile = useIsMobile();

  // Conversations + Messages
  const [conversations, setConversations] = useState(() => {
    const saved = localStorage.getItem("contact_conversations");
    return saved ? JSON.parse(saved) : [];
  });
  const [currentConversationId, setCurrentConversationId] = useState(() => {
    const saved = localStorage.getItem("contact_conversations");
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length ? parsed[0].id : null;
  });
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("contact_conversations");
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length ? (parsed[0].messages || []) : [];
  });

  const [isLoading, setIsLoading] = useState(false);
  const parseBillingHeader = (response) => {
    try {
      const raw = response?.headers?.get('x-ydim-billing');
      if (!raw) return null;
      return JSON.parse(decodeURIComponent(raw));
    } catch {
      return null;
    }
  };

  const [billingUi, setBillingUi] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Persist conversations
  useEffect(() => {
    localStorage.setItem("contact_conversations", JSON.stringify(conversations));
  }, [conversations]);

  // Helpers
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
  const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Generate / store userId
  const getUserId = () => {
    let uid = localStorage.getItem("contact_chatUserId");
    if (!uid) {
      uid = "user-" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("contact_chatUserId", uid);
    }
    return uid;
  };
  const userId = getUserId();

  // Input / Files
  const [inputValue, setInputValue] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]); // [{file, previewUrl, kind:'image'|'file'}]
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // Scroll behavior state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Check if user is at bottom of chat
  const checkIfAtBottom = () => {
    if (!chatMessagesRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
    const threshold = 100; // Allow 100px threshold for "at bottom"
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  // Handle scroll events
  const handleScroll = () => {
    const atBottom = checkIfAtBottom();
    setIsAtBottom(atBottom);
    setShowScrollButton(!atBottom);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set user scrolling state
    setIsUserScrolling(true);
    
    // Reset user scrolling state after 150ms of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 150);
  };

  // Auto-scroll to bottom (only if user hasn't scrolled up)
  const autoScrollToBottom = () => {
    if (isBotTyping) return; // ✅ يمنع الإكراه أثناء typing
    if (isAtBottom && !isUserScrolling) {
      scrollToBottom();
    }
  };
  

  // Manual scroll to bottom (when user clicks button)
  const manualScrollToBottom = () => {
    scrollToBottom();
    setIsAtBottom(true);
    setShowScrollButton(false);
    setIsUserScrolling(false);
  };

  // Scroll effect - only auto-scroll if at bottom
  useEffect(() => {
    autoScrollToBottom();
  }, [messages, isAtBottom, isUserScrolling]);

  // Add scroll event listener
  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => {
        chatContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle sidebar state based on mobile detection
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    } else {
      setIsSidebarOpen(true);
    }
  }, [isMobile]);

  // Handle overlay click to close sidebar on mobile
  const handleOverlayClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [inputValue]);

  // RTL/LTR detector
  const detectDirection = (text) => /[\u0600-\u06FF]/.test(text) ? 'rtl' : 'ltr';

  // Create new conversation
  const createNewConversation = () => {
    const newConv = {
      id: Date.now(),
      title: `Conversation ${conversations.length + 1}`,
      createdAt: new Date().toISOString(),
      messages: []
    };
    const updated = [newConv, ...conversations];
    setConversations(updated);
    setCurrentConversationId(newConv.id);
    setMessages([]);
  };

  // Load conversation
  const loadConversation = (id) => {
    setCurrentConversationId(id);
    const conv = conversations.find(c => c.id === id);
    setMessages(conv ? conv.messages : []);
  };

  // Delete conversation
  const deleteConversation = (id) => {
    const updated = conversations.filter((c) => c.id !== id);
    setConversations(updated);
    if (currentConversationId === id) {
      setCurrentConversationId(updated.length ? updated[0].id : null);
      setMessages(updated.length ? updated[0].messages : []);
    }
  };

  // File selection (multiple)
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const items = files.map(f => ({
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      kind: f.type.startsWith("image/") ? "image" : "file",
      name: f.name
    }));
    setUploadedFiles(prev => [...prev, ...items]);
    // reset input
    e.target.value = null;
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Actions click (from bot)
  const handleActionClick = async (action) => {
    if (action.type === 'button' && action.action === 'send_email_request') {
      try {
        const res = await fetch(buildApiUrl('/api/contact/action/send-email-request'), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            action: action.action,
            note: "User requested consultant verification"
          }),
        });
        const resultText = await res.text();

        // Add system confirmation as a bot message in the chat
        const confirmMsg = {
          id: Date.now(),
          text: "📧 تم إرسال الطلب للمستشار للتأكد من نظامية وصحة الرد.",
          isUser: false,
          sender: t('bot'),
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, confirmMsg]);
        setConversations(prev =>
          prev.map(c =>
            c.id === currentConversationId
              ? { ...c, messages: [...(c.messages || []), confirmMsg] }
              : c
          )
        );

        // (Optional) you can log resultText if needed
        console.log("send_email_request:", resultText);
      } catch (err) {
        console.error("❌ Error sending action:", err);
        const failMsg = {
          id: Date.now(),
          text: "❌ حدث خطأ أثناء إرسال الطلب للمستشار.",
          isUser: false,
          sender: t('bot'),
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, failMsg]);
        setConversations(prev =>
          prev.map(c =>
            c.id === currentConversationId
              ? { ...c, messages: [...(c.messages || []), failMsg] }
              : c
          )
        );
      }
    }
  };

  // Message toolbar functions
  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log('Message copied to clipboard');
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleLikeMessage = (messageId) => {
    console.log('Liked message:', messageId);
    // You could implement like functionality here
  };

  const handleDislikeMessage = (messageId) => {
    console.log('Disliked message:', messageId);
    // You could implement dislike functionality here
  };

  const handleShareMessage = (text) => {
    if (navigator.share) {
      navigator.share({
        title: 'Chat Message',
        text: text,
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      handleCopyMessage(text);
    }
  };

  // Send message
  const sendQuestion = async (messageText) => {
    const trimmedText = String(messageText || '').trim();
    if (trimmedText === '' && uploadedFiles.length === 0) return;

    setIsLoading(true);

    let convId = currentConversationId;
    // Create conversation if none
    if (!convId) {
      const title = trimmedText.slice(0, 30) || "New Chat";
      const newConv = {
        id: Date.now(),
        title,
        createdAt: new Date().toISOString(),
        messages: []
      };
      setConversations([newConv, ...conversations]);
      convId = newConv.id;
      setCurrentConversationId(convId);
      setMessages([]);
    }

    // Create user message
    const userMsg = {
      id: Date.now(),
      text: trimmedText,
      files: uploadedFiles, // keep list for rendering
      isUser: true,
      sender: userId,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Save to conversation
    setConversations(prev =>
      prev.map(c => {
        if (c.id === convId) {
          const updatedMessages = [...(c.messages || []), userMsg];
          // set title from first message
          const newTitle = c.messages?.length === 0
            ? (trimmedText.slice(0, 30) || c.title)
            : c.title;
          return { ...c, title: newTitle, messages: updatedMessages };
        }
        return c;
      })
    );

    // reset input
    const originalUserMessage = trimmedText;
    setInputValue('');
    setUploadedFiles([]);

    // Prepare formData (Ydim backend validates firm context and forwards to webhook)
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('message', userMsg.text || "");

    if (process.env.NODE_ENV === 'development' && isFirmMode) {
      console.log('[contact-send] activeFirmCase keys', activeFirmCase ? Object.keys(activeFirmCase) : null);
      console.log('[contact-send] activeFirmCase', activeFirmCase);
    }

    const firmCtx =
      isFirmMode && activeFirmCase ? buildFirmCaseContextPayload(activeFirmCase) : null;
    const includeFirmContext = !!firmCtx;
    if (firmCtx) {
      const firmJson = JSON.stringify(firmCtx);
      if (process.env.NODE_ENV === 'development') {
        console.log('[contact-send] firmCaseContext JSON keys', Object.keys(firmCtx));
        console.log('[contact-send] firmCaseContext appended', firmCtx);
      }
      formData.append('firmCaseContext', firmJson);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[contact]', {
        page: 'disc',
        firmCaseContextIncluded: includeFirmContext,
        firmContextSummaryIncluded: includeFirmContext,
        mode: includeFirmContext ? 'firm-context' : 'public/default',
      });
    }

    // append multiple files
    userMsg.files.forEach((fObj, idx) => {
      const key = fObj.kind === 'image' ? `ImageId_${Date.now()}_${idx}` : `FileId_${Date.now()}_${idx}`;
      formData.append(key, fObj.file, fObj.name);
    });

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.CONTACT_SEND), {
        method: "POST",
        credentials: 'include',
        body: formData,
      });
      const headerBillingUi = parseBillingHeader(response);

      // Try JSON first
      let parsed = {};
      let replyText = "";
      let actions = [];
      let serverBillingUi = null;
      try {
        parsed = await response.json();
        replyText = extractAssistantReplyText(parsed);
        actions = extractWebhookActions(parsed);
        serverBillingUi = extractWebhookBilling(parsed, headerBillingUi);
      } catch {
        // fallback text
        const asText = await response.text();
        replyText = asText || " ";
        actions = [];
        serverBillingUi = headerBillingUi;
      }

      if (!response.ok) {
        setBillingUi(serverBillingUi);
        if (response.status === 403) {
          const msg =
            (parsed &&
              typeof parsed === 'object' &&
              !Array.isArray(parsed) &&
              parsed.message) ||
            'تم بلوغ حد الاستخدام. سجّل الدخول أو رقّ خطتك للمتابعة.';
          const hardBlockMsg = {
            id: Date.now(),
            text: msg,
            isUser: false,
            sender: t('bot'),
            timestamp: new Date(),
            paywallHardBlocked: true,
          };
          setMessages((prev) => [...prev, hardBlockMsg]);
          setConversations((prev) =>
            prev.map((c) =>
              c.id === convId ? { ...c, messages: [...(c.messages || []), hardBlockMsg] } : c
            )
          );
        }
        setIsLoading(false);
        setIsBotTyping(false);
        return;
      }

      // Create bot placeholder (typing)
      const botMessageId = Date.now();
      setIsBotTyping(true);   // ✅ بدء typing
      scrollToBottom();       // ✅ نزول مرة واحدة فقط
      const preview = buildPaywallPreviewParts(replyText, serverBillingUi);

      const botResponse = {
        id: botMessageId,
        text: "",
        actions,
        showActions: false, // 👈 لا نعرض الأزرار إلا بعد اكتمال الكتابة
        isUser: false,
        sender: t('bot'),
        timestamp: new Date(),
        paywallPreview: preview.isPreview,
        paywallHiddenPart: preview.hiddenPart || '',
      };
      setMessages(prev => [...prev, botResponse]);

      // تم إلغاء تأثير الكتابة التدريجي لتجنب تجمد الواجهة وضمان استمرار العملية في الخلفية
      setMessages(prev =>
        prev.map(m =>
          m.id === botMessageId
            ? { ...m, text: preview.visiblePart, showActions: true }
            : m
        )
      );
      setBillingUi(serverBillingUi);

      // حفظ المحادثة فوراً لضمان بقائها عند التنقل بين الصفحات
      setConversations(prev =>
        prev.map(c =>
          c.id === convId
            ? {
                ...c,
                messages: (c.messages || []).map(m =>
                  m.id === botMessageId ? { ...m, text: preview.visiblePart, showActions: true } : m
                )
              }
            : c
        )
      );

      trackInteraction(originalUserMessage, replyText, 'chat', user ? user.id : null);

      setIsBotTyping(false);
      setIsLoading(false); // إنهاء حالة التحميل فور وصول الرد

      // also persist placeholder (initially empty text)
      setConversations(prev =>
        prev.map(c =>
          c.id === convId ? { ...c, messages: [...(c.messages || []), botResponse] } : c
        )
      );
    } catch (error) {
      console.error("❌ Fetch error:", error);
      setIsBotTyping(false);
      const errorMessage = {
        id: Date.now(),
        text: "❌ حدث خطأ أثناء الاتصال بالخادم",
        isUser: false,
        sender: t('bot'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setConversations(prev =>
        prev.map(c =>
          c.id === convId ? { ...c, messages: [...(c.messages || []), errorMessage] } : c
        )
      );
    }

    setIsLoading(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    await sendQuestion(inputValue);
  };

  // Auto-send question from landing page (?q=...)
  useEffect(() => {
    if (authLoading) return;

    const q = (searchParams.get('q') || '').trim();
    if (!q || autoSendDoneRef.current) return;

    autoSendDoneRef.current = true;
    setInputValue(q);
    setSearchParams({}, { replace: true });
    sendQuestion(q);
    // sendQuestion intentionally omitted — one-shot via autoSendDoneRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, searchParams, setSearchParams]);

  return (
    <div className={`unified-page ${isRTL ? 'rtl' : 'ltr'}`}>

      {/* Mobile Overlay */}
      {isMobile && (
        <div 
          className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
          onClick={handleOverlayClick}
        />
      )}
      
      {/* Sidebar */}
      <div ref={sidebarRef} className={`unified-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="new-chat-btn" onClick={createNewConversation}>
            <span className="plus-icon">+</span> {t('newChat')}
          </button>
          <button className="sidebar-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <span className="arrow-icon">{isSidebarOpen ? '←' : '→'}</span>
          </button>
        </div>

        <div className="conversations-list">
          {conversations.map(conversation => (
            <div
              key={conversation.id}
              className={`conversation-item ${currentConversationId === conversation.id ? 'active' : ''}`}
              onClick={() => loadConversation(conversation.id)}
            >
              <div className="conversation-content">
                <div className="conversation-title">{conversation.title}</div>
                <div className="conversation-date">{formatDate(conversation.createdAt)}</div>
              </div>
              <button
                className="delete-conversation-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConversation(conversation.id);
                }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat */}
      <div className={`unified-main ${isSidebarOpen ? 'with-sidebar' : 'full-width'}`}>

        <div className="unified-header">
          <button className="mobile-sidebar-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>☰</button>
          <h2>{t('contactSupport')}</h2>
        </div>
        {billingUi?.softGate?.active && !billingUi?.firmModeExempt && (
          <div className="soft-paywall-banner">
            <span>{billingUi?.softGate?.message || 'Usage limit reached for current plan.'}</span>
            <div className="soft-paywall-actions">
              {(!user || user.isGuest) ? (
                <>
                  <button type="button" onClick={() => navigate('/login')}>تسجيل الدخول</button>
                  <button type="button" onClick={() => navigate('/register')}>إنشاء حساب</button>
                  <button type="button" onClick={() => navigate('/pricing')}>الاطلاع على الأسعار</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setShowUpgradeModal(true)}>ترقية الخطة</button>
                  <button type="button" onClick={() => navigate('/pricing')}>عرض الباقات</button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="unified-messages" ref={chatMessagesRef}>
        <div className="case-journey">
  {t('journey.contact')}
</div>

          {/* Instruction message – shown only when chat is empty */}
{messages.length === 0 && (
  <div className="chat-instructions">
   {t('chat.instructions.general')}
  </div>
)}

          {messages.map(message => (
            <div key={message.id} className={`message ${message.isUser ? 'user-message' : 'bot-message'}`}>
              <div className="message-content">
                {message.text && (
                  <div
                    className="message-text"
                    data-soft-preview={message.paywallPreview ? 'true' : 'false'}
                    style={{
                      direction: detectDirection(
                        `${message.text}${message.paywallHiddenPart || ''}`
                      ),
                      textAlign:
                        detectDirection(`${message.text}${message.paywallHiddenPart || ''}`) ===
                        'rtl'
                          ? 'right'
                          : 'left',
                    }}
                  >
                    {formatMessageText(message.text)}
                    {message.paywallPreview && message.paywallHiddenPart ? (
                      <div className="paywall-preview-remainder" aria-hidden="true">
                        {formatMessageText(message.paywallHiddenPart)}
                      </div>
                    ) : null}
                    {message.paywallPreview ? <InlinePaywallPreviewCard /> : null}
                    {message.paywallHardBlocked ? <InlinePaywallPreviewCard variant="hard" /> : null}
                  </div>
                )}

                {/* Files inside message (for user messages) */}
                {Array.isArray(message.files) && message.files.length > 0 && (
                  <div className="message-files">
                    {message.files.map((f, idx) => (
                      <div key={idx}>
                        {f.kind === "image" ? (
                          <img src={f.previewUrl} alt={f.name} className="chat-image" />
                        ) : (
                          <div className="file-message">📎 {f.name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions AFTER typing only */}
                {message.actions && message.actions.length > 0 && message.showActions && (
                  <div className="message-actions">
                    {message.actions.map((action, idx) => (
                      <button
                        key={idx}
                        className="action-button"
                        onClick={() => handleActionClick(action)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Message toolbar for assistant messages only */}
                {!message.isUser && message.text && (
                  <div className="message-toolbar">
                    <button
                      className="toolbar-button"
                      onClick={() => handleCopyMessage(message.text)}
                      title={t('copyMessage')}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="toolbar-button"
                      onClick={() => handleLikeMessage(message.id)}
                      title={t('likeMessage')}
                    >
                      <ThumbsUp size={16} />
                    </button>
                    <button
                      className="toolbar-button"
                      onClick={() => handleDislikeMessage(message.id)}
                      title={t('dislikeMessage')}
                    >
                      <ThumbsDown size={16} />
                    </button>
                    <button
                      className="toolbar-button"
                      onClick={() => handleShareMessage(message.text)}
                      title={t('shareMessage')}
                    >
                      <Share size={16} />
                    </button>
                  </div>
                )}

                <div className="message-time">{formatTime(message.timestamp)}</div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="typing-indicator"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <FirmCaseContextBar />

        {/* Floating scroll to bottom button */}
        {showScrollButton && (
          <button
            className="scroll-to-bottom-btn"
            onClick={manualScrollToBottom}
            title={t('scrollToBottom')}
          >
            ⬇️
          </button>
        )}

        {/* Input Area */}
        <form className="unified-input-form" onSubmit={handleSendMessage}>
          {uploadedFiles.length > 0 && (
            <div className="uploaded-preview">
              {uploadedFiles.map((f, idx) => (
                <div key={idx} className="preview-item">
                  {f.kind === "image" ? (
                    <img src={f.previewUrl} alt={f.name} className="preview-image" />
                  ) : (
                    <div className="preview-file">📎 {f.name}</div>
                  )}
                  <button type="button" className="remove-file" onClick={() => removeUploadedFile(idx)}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="input-container">
            <label htmlFor="file-upload" className="file-upload-btn">+</label>
            <input id="file-upload" type="file" multiple onChange={handleFileChange} style={{ display: "none" }} />
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('typeMessage')}
              className="chat-input"
              rows={1}
              style={{
                direction: detectDirection(inputValue),
                textAlign: detectDirection(inputValue) === 'rtl' ? 'right' : 'left'
              }}
            />
            <button type="submit" className="send-button" disabled={isLoading || (inputValue.trim() === '' && uploadedFiles.length === 0)}>
              {t('send')}
            </button>
          </div>
        </form>
        {showUpgradeModal && user && !user.isGuest && (
          <UpgradeModal
            planId="pro"
            onClose={() => setShowUpgradeModal(false)}
            onSuccess={() => setShowUpgradeModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default LegalDiscussionPage;
